// The sea mine detonation looks of the World field `seaMineDetonation`
// (v2-the-deep.md §11.8, mockup round 070, R2538, R2652).
//
// One look draws what a sea mine draws where it goes off. Each look ports
// its card of `archive/mockups/070-sea-mine-detonations.html`: the burst at
// the sea mine, the slow tell a Bola leaves on the hull it struck, and the
// two tells of a Siren (the moment it hears the hull, and the front that
// runs out to it). Card 20 Round and Rock joins every look: when a round
// or rock sets off a sea mine, no damage figure and no slow tell show,
// because no hull is struck, and rock draws its chips. The round of a gun
// is the salvo of the combat layer, the shot look of a shot at a hull
// (R3031): the burst starts when its first round arrives, and the gun
// kill look (`gun-kill-looks.ts`, R3032) plays over it.
//
// These are the marks alone. The layer that draws sea objects on the map
// owns the timing: it keeps each detonation from the event, asks the look
// for its life, and hands the burst the seconds since the blast (`k`, from
// `-pre` for a look with a fuse). R is the outer edge of the effect: the
// splash of a Siren and of a Petard, just inside the contact radius of a
// Barb and of a Bola. `w` is the weight of the kind, its damage over the
// damage of the Petard.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { SEA_MINE_DETONATION_STYLES } from '../gen/balance';
import { seamarks as WORDS } from '../gen/strings';
import {
  SEA_INK, arc, bandPx, circle, disc, fade, line, lw, outCubic, smooth, text, wordFont, type Ctx, type Pt,
} from './seaobject-kit';
import { TAU, clamp01 } from './math';
import { rgba } from './color';
import type { SeaMineKind } from './seamine-marks';

/** One look of the `seaMineDetonation` field. */
export type SeaMineDetonationStyle = (typeof SEA_MINE_DETONATION_STYLES)[number];

/** What set the sea mine off: a hull, a gun round, or rock (§11.2). */
export type SeaMineTrigger = 'hull' | 'round' | 'rock';

/** One detonation, as the burst reads it. */
export interface SeaBurst {
  x: number;
  y: number;
  /** The outer edge of the effect, map px. */
  R: number;
  /** The weight of the kind, 0 to 1. */
  w: number;
  kind: SeaMineKind;
  /** The ink of the side that laid the sea mine. */
  col: number;
  /** The place of this sea mine in a chain: 0 is the one the trigger hit. */
  i: number;
  /** The seeded generator of this detonation: the burst draws the same
   *  scatter each frame, so the caller seeds it per detonation and per
   *  frame alike (`mulberry32` of the kit). */
  rnd: () => number;
  /** The hull that touched the sea mine, at the blast, or null. */
  hit: Pt | null;
  trigger: SeaMineTrigger;
  /** The map zoom: a hairline keeps 0.9 screen px at least. */
  z: number;
}

/** The hull a Bola slowed, now. */
export interface SlowHull {
  x: number;
  y: number;
  /** The heading, radians. */
  hdg: number;
}

/** What a slow tell is told. */
export interface SlowTell {
  /** The ink of the side that laid the Bola. */
  col: number;
  /** Where the Bola went off. */
  det: Pt;
  /** The clock in seconds. */
  t: number;
  /** The fade-in of the tell, 0 to 1. */
  a: number;
  z: number;
  /** The speed share of the Bola slow (seaobjects.toml), for the card
   *  that writes it. */
  slowMul: number;
  /** The font family a figure is written in (the mono stack). */
  face: string;
}

/** How a look shows the moment a Siren hears the hull. */
export type HearStyle = 'bearing' | 'bearing-dots' | 'bearing-solid' | 'noise' | 'ear' | 'ring';
/** How a look shows the front of a Siren that runs to the hull. */
export type ReachStyle = 'arc' | 'line' | 'dots' | 'rings';

/** One look: its life in seconds, its fuse, its two Siren tells, the burst
 *  and the slow tell. */
export interface SeaMineDetonationLook {
  life: number;
  /** The fuse: the burst draws from `-pre`, the look's own count. */
  pre: number;
  hear: HearStyle;
  reach: ReachStyle;
  burst(c: Ctx, o: SeaBurst, k: number): void;
  slow(c: Ctx, H: SlowHull, u: number, o: SlowTell): void;
}

const WHITE = SEA_INK.white, PALE = SEA_INK.pale, FAINT = SEA_INK.faint;

/** The bearing and the reach share of each drop of a burst, drawn anew
 *  from the look's seed each frame. The scratch holds the most drops a
 *  look draws (20), so a frame builds no array. */
const DROP_TH = new Float64Array(20), DROP_F = new Float64Array(20);
/** The drops of Foam Crown, the rays of Shock Star and of Spray Star, by
 *  kind. Shock Star draws the Bola apart and has no count for it. */
const CROWN_DROPS: Readonly<Record<SeaMineKind, number>> = { barb: 10, bola: 7, siren: 14, petard: 18 };
const SHOCK_RAYS: Readonly<Record<Exclude<SeaMineKind, 'bola'>, number>> = { barb: 6, siren: 5, petard: 8 };
const SPRAY_RAYS: Readonly<Record<SeaMineKind, number>> = { barb: 8, bola: 4, siren: 10, petard: 12 };
/** The three pulses of Bubble Pulse: from, to and the share of the reach. */
const PULSES = [[0, 0.42, 1], [0.42, 0.72, 0.56], [0.72, 0.94, 0.3]] as const;

function inHull(c: Ctx, H: SlowHull, fn: () => void): void {
  c.save(); c.translate(H.x, H.y); c.rotate(H.hdg); fn(); c.restore();
}

/** The colour of the layer flares at the sea mine for `d` seconds: the
 *  trigger moment. */
export function flare(c: Ctx, o: SeaBurst, k: number, d: number): void {
  if (k < 0 || k >= d) return;
  const u = k / d;
  disc(c, o.x, o.y, (o.kind === 'petard' ? 4 : 3) * (1 - u), o.col, 1 - u);
  circle(c, o.x, o.y, 5 + 3.5 * u, o.col, 1 - u, lw(1.2, o.z));
}

/** A sign of the kind (card 05), `s` its half size. */
export function kindSign(c: Ctx, kind: SeaMineKind, x: number, y: number, s: number, ink: number, a: number, w: number): void {
  if (kind === 'barb') {
    for (let i = 0; i < 3; i++) {
      const th = (i * Math.PI) / 3 + Math.PI / 2;
      line(c, x - Math.cos(th) * s, y - Math.sin(th) * s, x + Math.cos(th) * s, y + Math.sin(th) * s, ink, a, w);
    }
  } else if (kind === 'bola') {
    line(c, x - s * 0.7, y, x + s * 0.7, y, ink, a, w * 0.8);
    disc(c, x - s * 0.8, y, s * 0.4, ink, a); disc(c, x + s * 0.8, y, s * 0.4, ink, a);
  } else if (kind === 'siren') {
    disc(c, x, y, s * 0.3, ink, a);
    for (const f of [0.6, 1]) {
      arc(c, x, y, s * f, -0.7, 0.7, ink, a, w); arc(c, x, y, s * f, Math.PI - 0.7, Math.PI + 0.7, ink, a, w);
    }
  } else {
    circle(c, x, y, s * 0.85, ink, a, w);
    line(c, x - s, y, x + s, y, ink, a, w); line(c, x, y - s, x, y + s, ink, a, w);
  }
}

/** What a Siren tell is told: where the Siren lies, where the hull is now,
 *  the range at which this Siren hears that hull (50 px × its noise), the
 *  clock, the moment the Siren heard, the layer's ink and the zoom. */
export interface HearState {
  siren: Pt;
  hull: Pt;
  hearPx: number;
  t: number;
  tTrig: number;
  col: number;
  z: number;
}

/**
 * The moment a Siren hears the hull, in one of the styles a look names.
 * `k` is the time since the Siren heard it, negative before.
 */
export function paintHearTell(c: Ctx, style: HearStyle, s: HearState, k: number): void {
  const [Sx, Sy] = s.siren, [Hx, Hy] = s.hull, RH = s.hearPx, z = s.z, d = Math.hypot(Sx - Hx, Sy - Hy);
  if (style === 'bearing' || style === 'bearing-dots' || style === 'bearing-solid') {
    if (k < 0 || k > 0.5) return;
    const a = 1 - k / 0.5, dash = style === 'bearing' ? [3, 3] : style === 'bearing-dots' ? [0.4, 3] : [];
    line(c, Sx, Sy, Hx, Hy, WHITE, 0.9 * a, lw(style === 'bearing-solid' ? 1.3 : 1.1, z), dash);
    circle(c, Sx, Sy, 7 + 5 * (k / 0.5), WHITE, a, lw(1.1, z));
  } else if (style === 'noise') {
    // The noise of the hull runs out as dashed rings to the range at which
    // a Siren hears it.
    if (s.t < s.tTrig + 0.3) {
      for (let j = 0; j < 2; j++) {
        const q = ((s.t + j * 0.45) % 0.9) / 0.9;
        circle(c, Hx, Hy, RH * q, PALE, 0.26 * (1 - q), lw(1, z), [3, 4]);
      }
    }
    if (k >= 0 && k < 0.45) {
      const a = 1 - k / 0.45, b = Math.atan2(Sy - Hy, Sx - Hx);
      arc(c, Hx, Hy, d, b - 0.24, b + 0.24, WHITE, a, lw(1.6, z));
    }
  } else if (style === 'ear') {
    // The Siren turns two small arcs to the hull as it closes.
    if (k > 0.4) return;
    const b = Math.atan2(Hy - Sy, Hx - Sx), near = clamp01((160 - d) / (160 - RH));
    const a = k < 0 ? 0.2 + 0.6 * near : 1 - k / 0.4, ink = k >= 0 ? WHITE : s.col, g = k > 0 ? 1 + 0.8 * k : 1;
    arc(c, Sx, Sy, 7 * g, b - 0.65, b + 0.65, ink, a, lw(1.2, z));
    arc(c, Sx, Sy, 10.4 * g, b - 0.5, b + 0.5, ink, 0.8 * a, lw(1.2, z));
  } else {
    // The hearing range of the loud hull, dashed round it; the Siren rings
    // when it falls inside.
    if (k > 0.5) return;
    circle(c, Hx, Hy, RH, PALE, k < 0 ? 0.18 : 0.18 + 0.55 * (1 - k / 0.5), lw(1, z), [2, 5]);
    if (k >= 0) { const q = k / 0.5; circle(c, Sx, Sy, 6 + 6 * q, WHITE, 1 - q, lw(1.2, z)); }
  }
}

/**
 * The front of a Siren from the Siren to the hull it heard. `u` is the
 * share of the way run, `q` the time since it arrived (negative before).
 */
export function paintReachTell(c: Ctx, style: ReachStyle, siren: Pt, hull: Pt, u: number, q: number, z: number): void {
  const [Sx, Sy] = siren, dx = hull[0] - Sx, dy = hull[1] - Sy, d = Math.hypot(dx, dy) || 1, b = Math.atan2(dy, dx);
  const a = q < 0 ? 1 : Math.max(0, 1 - q / 0.35);
  if (style === 'arc') {
    if (q < 0) {
      arc(c, Sx, Sy, d * u, b - 0.32, b + 0.32, WHITE, 0.9, lw(1.4, z));
      arc(c, Sx, Sy, d * u * 0.8, b - 0.26, b + 0.26, PALE, 0.4, lw(1, z));
    }
  } else if (style === 'line') {
    const e = q < 0 ? u : 1;
    line(c, Sx, Sy, Sx + dx * e, Sy + dy * e, WHITE, 0.9 * a, lw(1.2, z));
  } else if (style === 'dots') {
    const n = Math.floor(d / 6);
    for (let i = 1; i < n; i++) {
      const f = i / n;
      if (f > u) break;
      disc(c, Sx + dx * f, Sy + dy * f, 0.9, WHITE, 0.85 * a);
    }
  } else if (q < 0) {
    for (let j = 0; j < 2; j++) {
      const f = u - j * 0.12;
      if (f > 0) circle(c, Sx + dx * f, Sy + dy * f, 2.5 - j * 0.6, WHITE, 0.85 - j * 0.3, lw(1, z));
    }
  }
}

// ---- card 20 Round and Rock, which joins every look ------------------------------

/**
 * The rock chips of card 20: a Bola that drifts onto rock chips it, and the
 * chips fly from the first sea mine of the chain. Every look calls it after
 * its burst; a sea mine that a hull or a round set off draws no chips.
 */
export function paintRockChips(c: Ctx, o: SeaBurst, k: number): void {
  if (o.trigger !== 'rock' || o.i !== 0 || k < 0) return;
  for (let i = 0; i < 5; i++) {
    const th = Math.PI * (0.2 + 0.15 * i), d = 2 + 4.5 * outCubic(k / 0.4);
    disc(c, o.x + Math.cos(th) * d, o.y + Math.sin(th) * d, 0.8, SEA_INK.neutral, fade(k, 0.4, 1.0));
  }
}

// ---- the eight looks ------------------------------------------------------------------

/** Card 01 Water Column: a dome of white water breaks and falls back to the
 *  edge as a ring of foam. */
const waterColumn: SeaMineDetonationLook = {
  life: 1.4, pre: 0, hear: 'bearing', reach: 'arc',
  burst(c, o, k) {
    const R = o.R, w = o.w, n = 8 + Math.round(12 * w), th = DROP_TH, f = DROP_F;
    for (let i = 0; i < n; i++) { th[i] = (i / n) * TAU + o.rnd() * 0.5; f[i] = 0.88 + 0.12 * o.rnd(); }
    flare(c, o, k, 0.14);
    if (k < 0.5) {
      const r = R * 0.7 * outCubic(k / 0.16), a = fade(k, 0.16, 0.5);
      disc(c, o.x, o.y, r, WHITE, 0.8 * a); circle(c, o.x, o.y, r, WHITE, a, lw(1.2, o.z));
    }
    if (k >= 0.12) {
      const dr = 0.6 + 0.5 * w, a = fade(k, 0.55, 1.25), s = outCubic((k - 0.12) / 0.55);
      for (let i = 0; i < n; i++) {
        const d = (R - dr) * (0.55 + 0.45 * s) * f[i];
        disc(c, o.x + Math.cos(th[i]) * d, o.y + Math.sin(th[i]) * d, dr * (1.2 - 0.4 * s), WHITE, 0.9 * a);
      }
    }
    if (k > 0.3) circle(c, o.x, o.y, R - 0.5, PALE, 0.5 * fade(k, 0.7, 1.4), lw(1, o.z), [2, 2]);
  },
  slow(c, H, u, o) {
    const a = o.a * (1 - u);
    inHull(c, H, () => {
      const L = 4 + 10 * (1 - u);
      for (const s of [-1, 1]) {
        line(c, -6, s * 7, -6 - L, s * 9, WHITE, 0.75 * a, lw(1.2, o.z));
        disc(c, -7.5 - L, s * 9.3, 0.9, WHITE, 0.7 * a);
      }
    });
  },
};

/** Card 02 Bubble Pulse: the gas bubble grows and collapses three times,
 *  smaller each time, then vents small bubbles. */
const bubblePulse: SeaMineDetonationLook = {
  life: 1.7, pre: 0, hear: 'noise', reach: 'rings',
  burst(c, o, k) {
    const R = o.R;
    flare(c, o, k, 0.12);
    for (let i = 0; i < PULSES.length; i++) {
      const q = PULSES[i];
      if (k < q[0] || k >= q[1]) continue;
      const u = (k - q[0]) / (q[1] - q[0]), r = (R - 0.7) * q[2] * Math.sin(Math.PI * u);
      disc(c, o.x, o.y, r, PALE, 0.16 - 0.04 * i);
      circle(c, o.x, o.y, r, i ? PALE : WHITE, 0.95 - 0.2 * i, lw(1.3 - 0.3 * i, o.z));
    }
    // The pop at the end of each pulse.
    for (let i = 0; i < PULSES.length; i++) {
      const q = k - PULSES[i][1];
      if (q >= 0 && q < 0.1) disc(c, o.x, o.y, (2.2 - 0.5 * i) * (1 - q / 0.1), WHITE, 0.95);
    }
    if (k > 0.9) {
      const n = 3 + Math.round(5 * o.w);
      for (let i = 0; i < n; i++) {
        const ox = (o.rnd() - 0.5) * R * 0.9, sp = 0.5 + 0.5 * o.rnd(), r0 = 0.7 + o.rnd() * 1.1, q = k - 0.9 - i * 0.05;
        if (q < 0) continue;
        circle(c, o.x + ox + Math.sin(q * 9 + i) * 1.2, o.y - Math.min(R * 0.55, q * 16 * sp), r0, WHITE, 0.8 * fade(q, 0.3, 0.75), lw(0.8, o.z));
      }
    }
  },
  slow(c, H, u, o) {
    const n = Math.ceil(6 * (1 - u)), a = o.a * (0.4 + 0.6 * (1 - u));
    for (let i = 0; i < n; i++) {
      const th = o.t * 1.1 + (i * TAU) / 6;
      circle(c, H.x + Math.cos(th) * 13, H.y + Math.sin(th) * 13, 1.5, WHITE, 0.85 * a, lw(0.9, o.z));
    }
  },
};

/** Card 03 Foam Crown: drops fly up in a crown and fall back as a ring of
 *  foam dots; the kind sets the count. */
const foamCrown: SeaMineDetonationLook = {
  life: 1.6, pre: 0, hear: 'ear', reach: 'dots',
  burst(c, o, k) {
    const R = o.R, n = CROWN_DROPS[o.kind], th = DROP_TH, f = DROP_F;
    for (let i = 0; i < n; i++) { th[i] = (i / n) * TAU + (o.rnd() - 0.5) * 0.3; f[i] = 0.86 + 0.14 * o.rnd(); }
    if (k < 0.1) disc(c, o.x, o.y, 3.5 * (1 - k / 0.1), WHITE, 1);
    flare(c, o, k, 0.12);
    const u = clamp01(k / 0.45), lift = Math.sin(Math.PI * u);
    for (let i = 0; i < n; i++) {
      const cs = Math.cos(th[i]), sn = Math.sin(th[i]);
      if (k < 0.45) {
        const d = (R - 1.6) * outCubic(u) * f[i], d0 = Math.max(0, d - 2 - 3 * lift);
        line(c, o.x + cs * d0, o.y + sn * d0, o.x + cs * d, o.y + sn * d, WHITE, 0.9, lw(0.8 + 0.8 * lift, o.z));
        disc(c, o.x + cs * d, o.y + sn * d, 0.7 + 0.8 * lift, WHITE, 0.95);
      } else {
        const d = (R - 1.4) * f[i];
        disc(c, o.x + cs * d, o.y + sn * d, 1.1, PALE, 0.85 * fade(k, 0.7, 1.6));
      }
    }
  },
  slow(c, H, u, o) {
    const a = o.a * (1 - u);
    inHull(c, H, () => {
      for (let i = 0; i < 4; i++) { const th = Math.PI * (0.78 + i * 0.148); disc(c, Math.cos(th) * 13, Math.sin(th) * 13, 1.2, WHITE, 0.85 * a); }
    });
  },
};

/** Card 05 Chart Stamp: a short flash, then a dotted danger circle at the
 *  edge with a sign of the kind, which stays 2 s. */
const chartStamp: SeaMineDetonationLook = {
  life: 2.8, pre: 0, hear: 'bearing-dots', reach: 'dots',
  burst(c, o, k) {
    const R = o.R;
    if (k < 0.12) disc(c, o.x, o.y, R * 0.5, WHITE, 0.9 * (1 - k / 0.12));
    if (k >= 0.05) {
      const r = (R - 0.7) * (0.7 + 0.3 * outCubic((k - 0.05) / 0.14)), a = 0.9 * fade(k, 2.0, 2.8);
      const n = Math.max(10, Math.round((TAU * r) / 3.2));
      for (let i = 0; i < n; i++) { const th = (i / n) * TAU; disc(c, o.x + Math.cos(th) * r, o.y + Math.sin(th) * r, 0.7, WHITE, a); }
    }
    if (k >= 0.08) kindSign(c, o.kind, o.x, o.y, Math.min(4.6, R * 0.42), o.col, smooth(0.08, 0.16, k) * fade(k, 2.0, 2.8), lw(1.2, o.z));
  },
  slow(c, H, u, o) {
    const a = o.a * fade(u, 0.92, 1), x = H.x - 12, y = H.y + 22;
    c.fillStyle = rgba(FAINT, 0.55 * a); c.fillRect(x, y, 24, 2.6);
    c.fillStyle = rgba(WHITE, 0.85 * a); c.fillRect(x, y, 24 * (1 - u), 2.6);
    // The ×0.5 is a note, placed in the world beside its bar (R3249).
    const wp = bandPx('mono', 'note', o.z);
    if (wp > 0) text(c, WORDS.mul({ mul: o.slowMul }), x + 27, y + 1.3, wordFont('mono', wp, o.face), PALE, 0.9 * a, 'left');
  },
};

/** Card 06 Horn Burst: the horns fly off in the colour of the layer; a Bola
 *  throws its two weights on a cord that wraps the hull. */
const hornBurst: SeaMineDetonationLook = {
  life: 1.2, pre: 0, hear: 'ear', reach: 'arc',
  burst(c, o, k) {
    const R = o.R;
    if (k < 0.12) disc(c, o.x, o.y, 4 * (1 - k / 0.12), WHITE, 1);
    const s = outCubic(k / 0.28), a = fade(k, 0.55, 1.15);
    if (o.kind === 'bola') {
      const L = (R - 1.6) * s, th = 7 * k, cx = Math.cos(th) * L, sy = Math.sin(th) * L;
      line(c, o.x - cx, o.y - sy, o.x + cx, o.y + sy, WHITE, 0.7 * a, lw(0.9, o.z));
      disc(c, o.x - cx, o.y - sy, 1.5, o.col, a); disc(c, o.x + cx, o.y + sy, 1.5, o.col, a);
    } else {
      const n = SHOCK_RAYS[o.kind], hl = o.kind === 'petard' ? 4 : 3;
      for (let i = 0; i < n; i++) {
        const th = (i / n) * TAU + 0.3 + 0.8 * k, r1 = 4 + (R - 4 - hl) * s, r2 = r1 + hl, cs = Math.cos(th), sn = Math.sin(th);
        line(c, o.x + cs * r1, o.y + sn * r1, o.x + cs * r2, o.y + sn * r2, o.col, a, lw(o.kind === 'petard' ? 1.8 : 1.4, o.z));
        if (o.kind === 'barb') {
          const bx = o.x + cs * r2, by = o.y + sn * r2;
          line(c, bx, by, bx - cs * 1.6 - sn * 1.4, by - sn * 1.6 + cs * 1.4, o.col, a, lw(1, o.z));
        }
        disc(c, o.x + cs * r2, o.y + sn * r2, 0.7, WHITE, a);
      }
    }
    if (k > 0.22 && k < 0.6) circle(c, o.x, o.y, R - 0.6, WHITE, 0.8 * (1 - (k - 0.22) / 0.38), lw(1, o.z));
  },
  slow(c, H, u, o) {
    const sp = 2.4 * TAU * (u - (u * u) / 2), drop = smooth(0.8, 1, u), r = 12 + 6 * drop, a = o.a * (1 - drop);
    const cx = Math.cos(sp) * r, sy = Math.sin(sp) * r;
    line(c, H.x - cx, H.y - sy, H.x + cx, H.y + sy, WHITE, 0.45 * a, lw(0.8, o.z));
    disc(c, H.x - cx, H.y - sy, 1.6, o.col, a); disc(c, H.x + cx, H.y + sy, 1.6, o.col, a);
  },
};

/** Card 13 Pip Fuse: three rings close on the sea mine and three pips
 *  light, then it bursts as a white cross. The fuse lasts 0.36 s. */
const pipFuse: SeaMineDetonationLook = {
  life: 1.0, pre: 0.36, hear: 'ear', reach: 'dots',
  burst(c, o, k) {
    const R = o.R;
    if (k < 0) {
      const q = k + 0.36, i = Math.min(2, Math.floor(q / 0.12)), u = (q - i * 0.12) / 0.12;
      circle(c, o.x, o.y, 9 - 4 * u, o.col, 0.95, lw(1.3, o.z));
      for (let j = 0; j < 3; j++) {
        const th = -Math.PI / 2 + (j - 1) * 0.7;
        disc(c, o.x + Math.cos(th) * 8, o.y + Math.sin(th) * 8, 0.9, j <= i ? WHITE : FAINT, 0.95);
      }
      return;
    }
    const e = outCubic(k / 0.1), a = fade(k, 0.12, 0.5);
    disc(c, o.x, o.y, R * 0.45 * e, WHITE, 0.95 * a);
    for (let i = 0; i < 4; i++) {
      const th = (i * Math.PI) / 2 + Math.PI / 4;
      line(c, o.x, o.y, o.x + Math.cos(th) * (R - 0.8) * e, o.y + Math.sin(th) * (R - 0.8) * e, WHITE, 0.95 * a, lw(1.6, o.z));
    }
    if (k > 0.06) circle(c, o.x, o.y, R - 0.6, PALE, 0.7 * fade(k, 0.3, 1.0), lw(1, o.z));
  },
  slow(c, H, u, o) {
    for (let i = 0; i < 3; i++) {
      const x = H.x + 23, y = H.y - 5 + i * 5;
      if (u < (3 - i) / 3) disc(c, x, y, 1.5, WHITE, 0.9 * o.a);
      else circle(c, x, y, 1.5, PALE, 0.35 * o.a, lw(0.8, o.z));
    }
  },
};

/** Card 14 Spray Star: white rays of spray shoot to the edge and fall back;
 *  the kind sets the count of rays. */
const sprayStar: SeaMineDetonationLook = {
  life: 0.95, pre: 0, hear: 'noise', reach: 'line',
  burst(c, o, k) {
    const R = o.R, n = SPRAY_RAYS[o.kind];
    const ro = (R - 0.6) * outCubic(k / 0.14), ri = (R - 0.6) * 0.92 * smooth(0.08, 0.6, k), a = fade(k, 0.4, 0.95);
    disc(c, o.x, o.y, 3, o.col, fade(k, 0.06, 0.16));
    for (let i = 0; i < n; i++) {
      const th = (i / n) * TAU + (o.kind === 'bola' ? Math.PI / 4 : 0), cs = Math.cos(th), sn = Math.sin(th);
      line(c, o.x + cs * ri, o.y + sn * ri, o.x + cs * ro, o.y + sn * ro, WHITE, 0.95 * a, lw(o.kind === 'petard' ? 1.6 : 1.1, o.z));
    }
  },
  slow(c, H, u, o) {
    const a = o.a * (1 - u);
    inHull(c, H, () => {
      line(c, -12, -4, -12, 4, WHITE, 0.8 * a, lw(1.3, o.z)); line(c, -16, -3, -16, 3, WHITE, 0.55 * a, lw(1.3, o.z));
    });
  },
};

/** Card 16 Reveal Ping: the hidden sea mine first shows its mark, then the
 *  white blast runs to the edge, and a cross and a dashed ring stay 3 s. */
const revealPing: SeaMineDetonationLook = {
  life: 3.4, pre: 0, hear: 'ring', reach: 'arc',
  burst(c, o, k) {
    const R = o.R, mr = o.kind === 'petard' ? 4 : 3;
    if (k < 0.32) {
      const s = Math.sin((Math.PI * k) / 0.32);
      disc(c, o.x, o.y, mr + 2 * s, o.col, 0.9); circle(c, o.x, o.y, 5 + 5 * s, o.col, 1, lw(1.6, o.z));
    }
    if (k >= 0.14) {
      const q = k - 0.14;
      disc(c, o.x, o.y, R * outCubic(q / 0.2), WHITE, 0.6 * fade(q, 0.1, 0.45));
      circle(c, o.x, o.y, (R - 0.6) * outCubic(q / 0.2), WHITE, 0.9 * fade(q, 0.3, 0.8), lw(1.1, o.z));
    }
    if (k >= 0.5) {
      const a = smooth(0.5, 0.7, k) * fade(k, 2.4, 3.4);
      line(c, o.x - 2.5, o.y - 2.5, o.x + 2.5, o.y + 2.5, o.col, a, lw(1.4, o.z));
      line(c, o.x - 2.5, o.y + 2.5, o.x + 2.5, o.y - 2.5, o.col, a, lw(1.4, o.z));
      circle(c, o.x, o.y, 6, o.col, 0.7 * a, lw(1, o.z), [2, 2]);
    }
  },
  slow(c, H, u, o) {
    const b = Math.atan2(o.det[1] - H.y, o.det[0] - H.x);
    arc(c, H.x, H.y, 19, b - 0.5, b + 0.5, o.col, 0.95 * o.a * (1 - u), lw(2.2, o.z));
  },
};

/** The look of each id. The record is typed by the list, so a look the
 *  list gains without its card is a type error. */
export const SEA_MINE_DETONATION_LOOKS: Record<SeaMineDetonationStyle, SeaMineDetonationLook> = {
  'reveal-ping': revealPing,
  'water-column': waterColumn,
  'bubble-pulse': bubblePulse,
  'foam-crown': foamCrown,
  'chart-stamp': chartStamp,
  'horn-burst': hornBurst,
  'pip-fuse': pipFuse,
  'spray-star': sprayStar,
};

/**
 * Paints one detonation in one look: the burst and the chips of card 20.
 * A sea mine that a round or rock set off struck no hull, so the caller
 * draws no slow tell and no damage figure for it.
 */
export function paintSeaMineDetonation(c: Ctx, style: SeaMineDetonationStyle, o: SeaBurst, k: number): void {
  SEA_MINE_DETONATION_LOOKS[style].burst(c, o, k);
  paintRockChips(c, o, k);
}
